import clsx from 'clsx';
import { CheckIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ZonePolygonPreview from './ZonePolygonPreview';

const ZONE_TYPE_LABELS = {
  general: 'General Area',
  restricted: 'Restricted Zone',
  hazard: 'Hazard Zone',
  crane_zone: 'Crane Zone',
  excavation: 'Excavation',
  chemical: 'Chemical Handling',
  electrical: 'Electrical Hazard',
  confined_space: 'Confined Space',
  hot_work: 'Hot Work Zone',
  safe_zone: 'Safe Zone',
};

const ZONE_TYPE_COLORS = {
  general: 'bg-blue-50 border-blue-200',
  restricted: 'bg-red-50 border-red-200',
  hazard: 'bg-yellow-50 border-yellow-200',
  crane_zone: 'bg-orange-50 border-orange-200',
  excavation: 'bg-amber-50 border-amber-200',
  chemical: 'bg-purple-50 border-purple-200',
  electrical: 'bg-rose-50 border-rose-200',
  confined_space: 'bg-slate-50 border-slate-200',
  hot_work: 'bg-red-50 border-red-200',
  safe_zone: 'bg-green-50 border-green-200',
};

const ZONE_TYPE_BADGE_COLORS = {
  general: 'badge-info',
  restricted: 'badge-error',
  hazard: 'badge-warning',
  crane_zone: 'badge-warning',
  excavation: 'badge-warning',
  chemical: 'badge-error',
  electrical: 'badge-error',
  confined_space: 'badge-neutral',
  hot_work: 'badge-error',
  safe_zone: 'badge-success',
};

const RISK_LEVEL_COLORS = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  critical: 'text-red-600',
};

const RISK_LEVEL_ICON = {
  low: '✓',
  medium: '⚠',
  high: '⚠⚠',
  critical: '🔴',
};

const hasPolygon = (zone) => {
  return zone?.polygon?.coordinates && zone.polygon.coordinates.length > 0;
};

export default function ZoneCard({ zone, isSelected, onSelect }) {
  const zoneTypeName = ZONE_TYPE_LABELS[zone.type] || zone.type;
  const badgeColor = ZONE_TYPE_BADGE_COLORS[zone.type] || 'badge-neutral';
  const riskColor = RISK_LEVEL_COLORS[zone.riskLevel] || RISK_LEVEL_COLORS.medium;
  const riskIcon = RISK_LEVEL_ICON[zone.riskLevel] || RISK_LEVEL_ICON.medium;

  return (
    <div
      onClick={onSelect}
      className={clsx(
        'p-4 border-2 rounded-lg cursor-pointer transition-all duration-200',
        isSelected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
      )}
    >
      <div className="flex gap-4">
        {/* Polygon Preview */}
        <div className="flex-shrink-0">
          <ZonePolygonPreview
            polygon={zone.polygon}
            color={zone.color}
            size={80}
          />
        </div>

        {/* Zone Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h4 className="font-semibold text-slate-900 truncate">
                {zone.name}
              </h4>
              {zone.code && (
                <p className="text-xs text-slate-500">{zone.code}</p>
              )}
            </div>
            {isSelected && (
              <CheckIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
            )}
          </div>

          {/* Type & Risk Badge */}
          <div className="flex gap-2 mb-2 flex-wrap items-center">
            <span className={clsx('badge text-xs', badgeColor)}>
              {zoneTypeName}
            </span>
            <span className={clsx('text-xs font-medium', riskColor)}>
              {riskIcon} Risk: {zone.riskLevel || 'medium'}
            </span>
            {!hasPolygon(zone) && (
              <span className="badge badge-warning text-xs">
                ✏️ Needs polygon
              </span>
            )}
          </div>

          {/* Zone Details */}
          <div className="text-sm text-slate-600 space-y-1">
            {zone.maxOccupancy && (
              <p>👥 Max Occupancy: {zone.maxOccupancy}</p>
            )}
            {zone.minSupervisors && (
              <p>👔 Min Supervisors: {zone.minSupervisors}</p>
            )}
            {zone.vehicleRestriction && (
              <p>
                🚗 Vehicles:{' '}
                <span className="font-medium">
                  {zone.vehicleRestriction}
                </span>
              </p>
            )}
          </div>

          {/* PPE Requirements */}
          {zone.ppeRequirements?.required && zone.ppeRequirements.required.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-600 mb-1">
                🛡️ Required PPE:
              </p>
              <div className="flex flex-wrap gap-1">
                {zone.ppeRequirements.required.map(ppe => (
                  <span
                    key={ppe}
                    className="inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded"
                  >
                    {ppe}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Alert Settings */}
          {(zone.alertOnEntry ||
            zone.alertOnExit ||
            zone.alertOnOvercrowd ||
            zone.alertOnPPE ||
            zone.alertOnVehicle) && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-600 mb-1">
                🔔 Alerts:
              </p>
              <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                {zone.alertOnEntry && <span>Entry</span>}
                {zone.alertOnExit && <span>Exit</span>}
                {zone.alertOnOvercrowd && <span>Overcrowd</span>}
                {zone.alertOnPPE && <span>PPE</span>}
                {zone.alertOnVehicle && <span>Vehicle</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
